import { useState } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { Box, Button, Stack, TintedStatusPill, Typography } from '../../ui/core';
import ConfirmModal from '../../components/shared/ConfirmModal';
import { useFormatters, useToast } from '../../hooks';
import useCities from '../../hooks/useCities';
import api from '../../lib/api';
import {
  DIRECTION_LABELS,
  PRODUCT_LABELS,
  REASON_PALETTES,
  createdByLabel,
  formatWindow,
  reasonCell,
} from './throttleFormatters';

const ActiveThrottlesTable = ({ rows, loading, onCancelled }) => {
  const f = useFormatters();
  const toast = useToast();
  const { cities } = useCities();
  const cityName = (cityId) => (cities.find((c) => c.id === cityId) || {}).cityName || cityId;
  const [rowToCancel, setRowToCancel] = useState(null);
  const [cancelling, setCancelling] = useState(false);

  const onConfirmCancel = async () => {
    setCancelling(true);
    try {
      await api.tripThrottling.cancelTripThrottle({ id: rowToCancel.id });
      setRowToCancel(null);
      toast.success('Trip throttle cancelled.');
      onCancelled && (await onCancelled());
    } catch (error) {
      setRowToCancel(null);
      toast.error(error.message);
    } finally {
      setCancelling(false);
    }
  };

  return (
    <Box>
      {!loading && rows.length === 0 ? (
        <Typography color="text.secondary" sx={{ fontSize: '13px' }}>
          No active trip throttles.
        </Typography>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>City</TableCell>
                <TableCell>Day</TableCell>
                <TableCell>Direction</TableCell>
                <TableCell>Window</TableCell>
                <TableCell>Products</TableCell>
                <TableCell>Reason</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="right">Cancel</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>{cityName(row.cityId)}</TableCell>
                  <TableCell>{f.date.toLocaleString(row.serviceDate, f.date.DateFormats.DATE_MED)}</TableCell>
                  <TableCell>{DIRECTION_LABELS[row.direction] || row.direction}</TableCell>
                  <TableCell>{formatWindow(row.windowStart, row.windowEnd)}</TableCell>
                  <TableCell>{PRODUCT_LABELS[row.productScope] || row.productScope}</TableCell>
                  <TableCell>
                    <TintedStatusPill palette={REASON_PALETTES[row.reason]} label={reasonCell(row)} />
                  </TableCell>
                  <TableCell>
                    <Stack spacing="2px">
                      <Typography sx={{ fontSize: '13px' }}>{createdByLabel(row)}</Typography>
                      <Typography color="text.secondary" sx={{ fontSize: '12px' }}>
                        {f.date.toLocaleString(row.createdAt, f.date.DateFormats.DATETIME_MED)}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell align="right">
                    <Button size="small" variant="outlined" color="error" onClick={() => setRowToCancel(row)}>
                      Cancel pause
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <ConfirmModal
        open={!!rowToCancel}
        title="Cancel trip throttle"
        message="Cancelling immediately reopens new-trip booking for this scope."
        confirmLabel={cancelling ? 'Cancelling…' : 'Cancel throttle'}
        confirmColor="error"
        confirmDisabled={cancelling}
        onConfirm={onConfirmCancel}
        onCancel={() => setRowToCancel(null)}
      />
    </Box>
  );
};

export default ActiveThrottlesTable;
